import { useBreakpoint } from '@insify/react-hooks';
import { animated, useTransition } from '@react-spring/web';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { HelpCircle } from '../../icons';

import * as styles from './Tooltip.module.css';
import { TooltipContent } from './components/TooltipContent';

import type React from 'react';

export type TooltipLocation = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

type Props = {
  anchor?: React.ReactNode;
  children?: React.ReactNode;
  anchorClassName?: string;
  className?: string;
  location?: TooltipLocation;
};

export const Tooltip = ({
  children,
  anchorClassName,
  className = '',
  location = 'bottom-right',
  anchor,
}: Props): JSX.Element => {
  const [show, setShow] = useState<boolean>(false);
  const [transform, setTransform] = useState<string>();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isMobile = useBreakpoint('(max-width: 759px)');
  const transitions = useTransition(show, {
    from: { opacity: '0', bottom: '-100%' },
    enter: { opacity: '1', bottom: '0%' },
    leave: { opacity: '0', bottom: '-100%' },
    config: { friction: 20, mass: 0.9 },
  });

  const closeOnEscape = useCallback((event: KeyboardEvent): void => {
    if (event.key === 'Escape') setShow(false);
  }, []);

  const handleClickOutside = useCallback((event: MouseEvent): void => {
    if (!tooltipRef.current?.contains(event.target as Node)) setShow(false);
  }, []);

  const calcTransform = useCallback((): void => {
    if (!anchorRef.current || !tooltipRef.current) return;

    const { top, bottom, right, left } = anchorRef.current.getBoundingClientRect();
    const { clientWidth: tooltipWidth } = tooltipRef.current;
    let x: string;

    switch (location) {
      case 'top-left':
        x = tooltipWidth + 5 > right ? `${left}px` : `calc(${right}px - 100%)`;
        return setTransform(`translate(${x}, calc(${top + scrollY - 5}px - 100%))`);
      case 'top-right':
        x = left + tooltipWidth > innerWidth ? `calc(${innerWidth - 5}px - 100%)` : `${left}px`;
        return setTransform(`translate(${x}, calc(${top + scrollY - 5}px - 100%))`);
      case 'bottom-left':
        x = tooltipWidth + 5 > right ? `${left}px` : `calc(${right}px - 100%)`;
        return setTransform(`translate(${x}, ${bottom + scrollY + 5}px)`);
      case 'bottom-right':
        x = left + tooltipWidth > innerWidth ? `calc(${innerWidth - 5}px - 100%)` : `${left}px`;
        return setTransform(`translate(${x}, ${bottom + scrollY + 5}px)`);
    }
  }, []);

  const removeAllEventListeners = () => {
    removeEventListener('keydown', closeOnEscape);
    removeEventListener('mousedown', handleClickOutside);
    removeEventListener('resize', calcTransform);
  };

  useEffect(() => () => removeAllEventListeners(), []);

  useEffect(() => {
    if (show) {
      addEventListener('keydown', closeOnEscape);
      addEventListener('mousedown', handleClickOutside);
    } else removeAllEventListeners();
  }, [show]);

  useEffect(() => {
    if (show && !isMobile) {
      calcTransform();
      addEventListener('resize', calcTransform);
    } else removeEventListener('resize', calcTransform);
  }, [show, isMobile]);

  return (
    <>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <span
        data-testid='tooltip'
        onClick={() => setShow(true)}
        className={clsx(styles.anchor, anchorClassName, className)}
        ref={anchorRef}
      >
        {anchor || <HelpCircle data-testid='tooltip-icon' className={styles.tooltipIcon} color='#000000' />}
      </span>
      {ReactDOM.createPortal(
        transitions(
          ({ opacity, bottom }, item) =>
            item && (
              <animated.div
                ref={tooltipRef}
                className={clsx(styles.tooltip, isMobile ? styles.mobile : styles.desktop, className)}
                style={{
                  bottom: isMobile ? bottom : 'initial',
                  transform: isMobile ? 'initial' : transform,
                  opacity: isMobile ? '1' : opacity,
                }}
              >
                <TooltipContent content={children} onClose={() => setShow(false)} />
              </animated.div>
            ),
        ),
        document.body,
      )}
    </>
  );
};
